/**
 * Canonical precision constants — SINGLE SOURCE OF TRUTH.
 *
 * All other packages (`@packages/shared`, `@packages/domain-legacy`,
 * `src/utils/apiDecimalConfig.ts`) re-export from here. Change a value
 * once here and it propagates everywhere.
 */

/** Standard decimal places for monetary values (prices, amounts, grant values). */
export const PRECISION_STANDARD = 4;

/** Standard decimal places for currency display (USD formatting). */
export const PRECISION_CURRENCY = 4;

/** Extended decimal places for percentages in fair value / financial models. */
export const PRECISION_PERCENTAGE_EXTENDED = 8;

/** Alias for PRECISION_STANDARD — used by legacy API interceptor and formatters. */
export const ACCEPT_DECIMAL = PRECISION_STANDARD;

/** Alias for PRECISION_CURRENCY — used by legacy currency formatters. */
export const CURRENCY_DECIMAL = PRECISION_CURRENCY;

/**
 * Engine-wide defaults for numeric precision.
 *
 * JSON field definitions can override any of these per-field via `inputPrecision`,
 * `displayPrecision`, etc. When omitted, the engine falls back to these values.
 *
 * FormatRegistry formatters reference these constants as defaults when
 * `FormatOptions.precision` is not provided.
 */
export const ENGINE_DEFAULTS = {
  INPUT_PRECISION: PRECISION_STANDARD,
  DISPLAY_PRECISION: PRECISION_STANDARD,
  CURRENCY_DISPLAY_PRECISION: PRECISION_CURRENCY,
  PERCENTAGE_DISPLAY_PRECISION: PRECISION_PERCENTAGE_EXTENDED,
  SHARES_DISPLAY_PRECISION: 0,
  ROUNDING_MODE: 'halfUp' as const,
} as const;

export { roundToDecimalPlaces } from './rounding';
export type { RoundingMode } from './rounding';

export interface ResolveInputPrecisionOptions {
  /** Per-field domain JSON `inputPrecision`. */
  fieldInputPrecision?: number;
  /** Form-level orchestrator `defaults.inputPrecision`. */
  formInputPrecision?: number;
  /** JSON Schema `type` — integers default to 0 dp when no field override. */
  fieldType?: string;
}

/**
 * Three-tier input precision: Field → Form defaults → Engine defaults.
 * Integer fields without an explicit override use 0 decimal places.
 */
export function resolveInputPrecision(opts: ResolveInputPrecisionOptions): number {
  if (opts.fieldInputPrecision != null) return opts.fieldInputPrecision;
  if (opts.formInputPrecision != null) return opts.formInputPrecision;
  if (opts.fieldType === 'integer') return ENGINE_DEFAULTS.SHARES_DISPLAY_PRECISION;
  return ENGINE_DEFAULTS.INPUT_PRECISION;
}

/**
 * Resolves which displayPrecision ViewField should apply.
 *
 * Precedence:
 * 1. Schema `overridePrecision` (always wins — DEV opt-in / view-specific override)
 * 2. When `stripDefPrecision` is true (feature flag): ignore primitive default
 * 3. Otherwise: FieldDef.displayPrecision from domain primitives
 *
 * Forms do not use this path — they use FieldDef.formatter / input rules.
 */
export function resolveDisplayPrecision(opts: {
  defPrecision?: number;
  overridePrecision?: number;
  /** When true, ignore primitive displayPrecision unless overridePrecision is set. */
  stripDefPrecision?: boolean;
}): number | undefined {
  if (opts.overridePrecision != null) return opts.overridePrecision;
  if (opts.stripDefPrecision === true) return undefined;
  return opts.defPrecision;
}

/**
 * View-engine display policy derived from REACT_APP_ENVIRONMENT.
 *
 * When true, ViewField strips FieldDef.displayPrecision unless the schema sets
 * overridePrecision. Forms are unaffected.
 *
 * Enabled for local / development / qa. Off for uat / production.
 */
const NATURAL_PRECISION_ENVIRONMENTS = new Set(['local', 'development', 'qa']);

export function isViewNaturalPrecisionEnabled(): boolean {
  const env = process.env.REACT_APP_ENVIRONMENT;
  return env != null && NATURAL_PRECISION_ENVIRONMENTS.has(env);
}
